import { tv, type VariantProps } from 'tailwind-variants'

export const DEFAULT_VARIANT = 'primary' as const
export const DEFAULT_SIZE = 'md' as const

/**
 * Ømni Button — Figma 27:448 (file 4WtjXCEzWf2lYBawEUd3p3), `is customer=no`.
 *
 * Geometry is measured, not derived: heights 48/40/32, radius 12/10/8,
 * px 20/16/12, py 12/10/8, gap 8/8/4. The declared py and height disagree by
 * 1px on md and 2px on sm in the source (10+21+10=41, 8+18+8=34); both values
 * are kept because `border-box` lets the height win, so the rendered box
 * matches the measured frame while the padding stays as designed.
 *
 * Control typography uses fixed theme tokens so labels keep fitting at every
 * breakpoint. Select and DatePicker retain their responsive field typography.
 * Button radii use their own tokens because the general radius scale differs.
 *
 * Icons next to text follow its line-height. Icon-only buttons instead use
 * fixed glyph boxes: sm 18px, md 20px, lg 24px, within 32/40/48px controls.
 */
const buttonVariants = tv(
  {
    slots: {
      root: [
        'font-body group/button relative inline-flex cursor-pointer items-center justify-center',
        'whitespace-nowrap border font-semibold transition-colors',
        'no-underline',
        /* The design's focus state is a fill swap with no ring, but measured
           against its own resting fill that swap is 2.5:1 on primary and 1.32:1
           on tertiary — under the 3:1 WCAG 1.4.11 needs to signal a state, and
           it is the only focus affordance there is. So the swap stays and an
           outline rides along on keyboard focus only. `outline` rather than
           `ring`: ring-offset paints an opaque gap in the offset colour, which
           shows as a white halo on any non-white surface, while outline-offset
           leaves it transparent. One colour across all three hierarchies keeps
           focus reading the same everywhere.

           `not-disabled:` on these is specificity, not semantics (a disabled
           button cannot take focus): `:not(:disabled)` carries its argument's
           specificity, so it ties hover's disabled/loading guard plus :hover, and
           Tailwind emits focus-visible after hover — focus wins the tie. Without
           it, tabbing with the cursor parked on a button showed nothing.

           The disabled guards use :not rather than :enabled, so these rules
           survive being applied to a non-form element through
           `buttonClassName`: `:enabled` only ever matches form controls, so on
           an anchor every hover, active and focus rule would silently never
           fire. Disabled appearance requires `data-disabled` without
           `data-loading`. */
        'not-disabled:focus-visible:outline-primary-200 not-disabled:focus-visible:outline-2 not-disabled:focus-visible:outline-offset-2',
        'data-[disabled]:not-data-[loading]:cursor-not-allowed',
        'data-[loading]:cursor-wait'
      ],
      /* `relative` on the two content layers is load-bearing: the ripple is an
         absolutely-positioned earlier sibling, and a positioned box paints over
         a static one regardless of DOM order. Positioning these puts them back
         on top of the wave. */
      content: 'relative inline-flex items-center transition-opacity',
      actions:
        'relative inline-flex items-center gap-2 self-stretch transition-opacity',
      divider: 'w-px self-stretch rounded-full opacity-30',
      caret: [
        'transition-transform duration-200',
        'group-aria-expanded/button:rotate-180',
        'group-data-[popup-open]/button:rotate-180'
      ],
      spinner: 'absolute inset-0 flex items-center justify-center gap-[0.25em]'
    },
    variants: {
      variant: {
        primary: {
          root: [
            'bg-primary text-on-primary border-transparent',
            'not-[:disabled,[data-loading]]:hover:bg-button-hover',
            'not-[:disabled,[data-loading]]:active:bg-button-active',
            'not-disabled:focus-visible:bg-button-focus',
            'data-[disabled]:not-data-[loading]:bg-button-disabled'
          ],
          divider: 'bg-primary-100'
        },
        secondary: {
          root: [
            'bg-button-secondary border-button-secondary-border text-surface-title-50',
            // Keep popup styling below hover/focus specificity and off disabled controls.
            '[&[data-popup-open]:where(:not(:disabled,[data-loading]))]:bg-button-secondary-active [&[data-popup-open]:where(:not(:disabled,[data-loading]))]:border-button-secondary-active-border',
            'not-[:disabled,[data-loading]]:hover:bg-button-secondary-hover not-[:disabled,[data-loading]]:hover:border-button-secondary-hover-border',
            'not-[:disabled,[data-loading]]:active:bg-button-secondary-active not-[:disabled,[data-loading]]:active:border-button-secondary-active-border',
            'not-disabled:focus-visible:bg-button-secondary-focus not-disabled:focus-visible:border-button-secondary-focus-border not-disabled:focus-visible:text-on-primary',
            'data-[disabled]:not-data-[loading]:border-button-secondary-disabled-border data-[disabled]:not-data-[loading]:text-button-secondary-disabled-text'
          ],
          divider: 'bg-surface-title-50'
        },
        tertiary: {
          root: [
            'text-surface-title-50 border-transparent bg-transparent',
            'not-[:disabled,[data-loading]]:hover:bg-button-tertiary-hover',
            'not-[:disabled,[data-loading]]:active:bg-button-tertiary-active',
            'not-disabled:focus-visible:bg-button-tertiary-focus',
            'data-[disabled]:not-data-[loading]:border-button-secondary-disabled-border data-[disabled]:not-data-[loading]:text-button-secondary-disabled-text'
          ],
          divider: 'bg-surface-title-50'
        }
      },
      size: {
        lg: {
          root: 'rounded-button-lg text-control-lg h-12 gap-2',
          content: 'gap-2'
        },
        md: {
          root: 'rounded-button-md text-control-md h-10 gap-2',
          content: 'gap-2'
        },
        sm: {
          root: 'rounded-button-sm text-control-sm h-8 gap-1',
          content: 'gap-1'
        },
        /* Deprecated, and deliberately frozen at the pre-Ømni geometry rather
           than folded into `sm`: aliasing them would silently resize the 18
           buttons still on them (`icon` was a 24px square, `sm` is 32; `xs`
           carried 24px of side padding against `sm`'s 12) including one in the
           pay-invoice form and the Drawer's own close button. They carry their
           own padding, so the row-padding compounds below skip them. */
        xs: {
          root: 'rounded-md px-6 py-0.5 text-[0.625rem]',
          content: 'gap-2'
        },
        icon: { root: 'size-6 rounded-md p-0', content: 'gap-2' }
      },
      /* Height comes from `size`; the ratio turns it into a square, so the
         48/40/32 of the design is authored exactly once. */
      iconOnly: { true: { root: 'aspect-square shrink-0' } }
    },
    /* With tv's own tailwind-merge disabled (see the config below), no two flat
       utilities from the same group may be emitted for one combination — CSS
       source order, not class order, would decide the winner. So the row
       padding lives here rather than on `size`: a square has none. */
    compoundVariants: [
      { size: 'lg', iconOnly: false, class: { root: 'px-5 py-3' } },
      { size: 'md', iconOnly: false, class: { root: 'px-4 py-2.5' } },
      { size: 'sm', iconOnly: false, class: { root: 'px-3 py-2' } },

      {
        size: ['sm', 'md', 'lg'],
        iconOnly: false,
        class: { root: '[&_[data-slot=icon]]:text-[1lh]' }
      },
      {
        size: 'sm',
        iconOnly: true,
        class: {
          root: '[&_[data-slot=icon]]:size-[18px] [&_[data-slot=icon]]:text-[18px]'
        }
      },
      {
        size: 'md',
        iconOnly: true,
        class: {
          root: '[&_[data-slot=icon]]:size-[20px] [&_[data-slot=icon]]:text-[20px]'
        }
      },
      {
        size: 'lg',
        iconOnly: true,
        class: {
          root: '[&_[data-slot=icon]]:size-[24px] [&_[data-slot=icon]]:text-[24px]'
        }
      },
      {
        size: ['sm', 'md', 'lg'],
        class: { root: '[&_[data-slot=icon]]:leading-none' }
      }
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      size: DEFAULT_SIZE,
      iconOnly: false
    }
  },
  /* Merging is disabled so the emitted class list is exactly what is authored
     here — the same convention the other Ømni atoms follow. It is what makes
     the "one flat utility per group" rule above load-bearing; the components still use cn() to merge whatever className the consumer passes, using the project registry
     that knows acqua's custom scales. */
  { twMerge: false }
)

/** @deprecated Ømni has three sizes. Frozen at their old geometry until swept. */
export type LegacyButtonSize = 'xs' | 'icon'

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>
export type ButtonSize = Exclude<
  NonNullable<VariantProps<typeof buttonVariants>['size']>,
  LegacyButtonSize
>

type ButtonSlots = Record<
  'root' | 'content' | 'actions' | 'divider' | 'caret' | 'spinner',
  string
>

/* tv allocates a closure per slot and re-walks the whole compound table on
   every slot call, which benchmarked at 3.6µs per render against 1.6µs for the
   flat config this replaced. The output is a pure function of these three
   variants — 30 combinations in total — so resolve each once and hand back
   plain strings. */
const slotCache = new Map<string, ButtonSlots>()

export const resolveSlots = (
  variant: ButtonVariant,
  size: ButtonSize | LegacyButtonSize,
  iconOnly: boolean
): ButtonSlots => {
  const key = `${variant}|${size}|${iconOnly}`
  const cached = slotCache.get(key)
  if (cached) return cached

  const slots = buttonVariants({ variant, size, iconOnly })
  const resolved: ButtonSlots = {
    root: slots.root(),
    content: slots.content(),
    actions: slots.actions(),
    divider: slots.divider(),
    caret: slots.caret(),
    spinner: slots.spinner()
  }
  slotCache.set(key, resolved)
  return resolved
}

/**
 * The root class string for an element that must *look* like a Button but
 * cannot *be* one — an anchor, chiefly, since navigation semantics (cmd-click,
 * opening in a new tab, being announced as a hyperlink) are not something a
 * `<button>` can fake and this component is always a `<button>`.
 *
 * Deliberately narrower than the `tv` instance behind it: only the root slot,
 * only the three live sizes, and it rides `resolveSlots`' cache rather than
 * re-walking the compound table per render. Interactive states work here
 * because the variants gate on `:not(:disabled)` — see the note above.
 */
export const buttonClassName = ({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  iconOnly = false
}: {
  variant?: ButtonVariant
  size?: ButtonSize
  iconOnly?: boolean
} = {}): string => resolveSlots(variant, size, iconOnly).root

export type ButtonVariantOptions = Omit<
  VariantProps<typeof buttonVariants>,
  'size' | 'iconOnly'
>
